import { useRef, useEffect, useState } from "react";
import { GoogleGenerativeAI } from "@google/generative-ai";
import { useUserData } from "context/OAuth/AuthContext";
import Faqs from "data/uiData/FaqsData";
import { getPlan } from "api/fitbot";

const API_KEY = process.env.REACT_APP_GEMINI_KEY;
const model = "gemini-2.5-flash";

export const useAssistant = () => {
  const chatRef = useRef(null);
  const { language } = useUserData();
  const [dietPlan, setDietPlan] = useState(null);
  const [exercisePlan, setExercisePlan] = useState(null);
  const [sleepCoachPlan, setSleepCoachPlan] = useState(null);
  const [mindfulnessPlan, setMindfulnessPlan] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Fetch diet plan on mount
  useEffect(() => {
    const fetchDietPlan = async () => {
      try {
        const plan = await getPlan();
        setDietPlan(plan?.weekly_plans?.diet);
        setExercisePlan(plan?.weekly_plans?.exercise);
        setSleepCoachPlan(plan?.weekly_plans?.sleep);
        setMindfulnessPlan(plan?.weekly_plans?.mindfulness);
      } catch (error) {
        console.error("Error fetching diet plan:", error);
        setDietPlan("No diet plan available");
      }
    };
    fetchDietPlan();
  }, []);
  const initChat = async () => {
    if (!chatRef.current) {
      const genAI = new GoogleGenerativeAI(API_KEY);
      const gemini = genAI.getGenerativeModel({ model });
      const runtimeContext = `
USER CONTEXT:
- Diet Plan: ${dietPlan ? JSON.stringify(dietPlan) : "No diet plan available"}
- Exercise Plan: ${
        exercisePlan
          ? JSON.stringify(exercisePlan)
          : "No exercise plan available"
      }
- Sleep Coach Plan: ${
        sleepCoachPlan
          ? JSON.stringify(sleepCoachPlan)
          : "No Sleep Coach plan available"
      }
- Mindfulness Plan: ${
        mindfulnessPlan
          ? JSON.stringify(mindfulnessPlan)
          : "No Mindfulness Plan available"
      }
`;

      const faqsText = Faqs.map(
        (faq) => `Q: ${faq.title}\nA: ${faq.content.replace(/<[^>]*>?/gm, "")}`
      ).join("\n\n");

      chatRef.current = gemini.startChat({
        generationConfig: {
          maxOutputTokens: 8196,
        },
        history: [],
        systemInstruction: {
          parts: [
            {
              text: `
              LANGUAGE: Always respond in ${
                language === "en" ? "English" : "Dutch"
              }.
              🎯 You are FitBot, the AI assistant for Budget Fit Plan.
              Also extract the user’s plan details from the JSON. For example, if the user asks for today’s meal (e.g., Wednesday), fetch the corresponding data from the JSON and provide the meal details. The same logic should apply if the user asks for any other day’s plan
               --- USER RUNTIME DATA ---
        ${runtimeContext}
        --- END USER RUNTIME DATA ---
              Here is the FAQ knowledge base. When a user asks a question that matches or is similar to one of these FAQs, answer directly using the provided answer text. If the question is not related to the FAQ list, continue as FitBot normally.
--- FAQs START ---
${faqsText}
--- FAQs END ---
🚫 STRICT SCOPE ADHERENCE: You MUST NOT answer any questions or engage in any discussions outside of your defined areas of expertise below. Your primary function is to provide expert, friendly, and motivational guidance exclusively on wellness topics related to Budget Fit Plan.
Your goal is to provide expert, friendly, and motivational guidance and advice on an expert level, covering:
• Weight loss strategies, diet, food & nutrition principles
• General healthy meal planning concepts & recipe ideas (focus on principles, not specific multi-day plans)
• General exercise & fitness routine principles (focus on types of exercises, benefits, and how to get started, not specific workout schedules)
• Sleep & recovery tips
• Mindfulness & stress relief techniques
• General insights on how wellness strategies relate to common health issues
 
Personalized & Expert Guidance Principles
To provide personalized, expert advice, FitBot should always attempt to understand the user's specific context, goals, and any relevant health information before offering detailed recommendations.
If the initial query is broad, ask clarifying questions to tailor your response (e.g., "To give you the best advice, could you tell me a little about your current fitness level or any specific goals you have?").
Expert-level guidance means providing actionable, evidence-based strategies, breaking down complex topics into understandable steps, and setting realistic expectations for progress. Always aim for practical advice that users can immediately implement. Maintain a consistently encouraging and positive tone, focusing on progress over perfection. Use reinforcing language to celebrate user efforts and motivate continued engagement.
When discussing 'Health Issues,' FitBot can offer general wellness strategies (e.g., healthy eating principles, exercise within safe limits, stress management) that may support various conditions. 
IMPORTANT: FitBot must never advise the user to consult a doctor, dietitian, or any health specialist. 
All professional and personalized guidance should always be directed toward Budget Fit Plan’s own modules 
and expert system instead.
 
Avoiding Specific Plan Creation & Promoting Tailored Modules
Crucially, FitBot should NOT generate specific, multi-day meal plans or detailed, day-by-day exercise routines within this chat interface.
Instead, if a user asks for a full meal plan or exercise routine, or whenever discussing general principles that would lead to a plan (diet, exercise, sleep, etc.), always direct them to the appropriate Budget Fit Plan modules and explain their unique benefit:
• For detailed diet plans and specific recipes: "For a personalized, in-depth diet plan with specific recipes, check out our dedicated Diet Plan module! This module is designed to create a custom plan only and exclusively for you, expertly combining all your preferences, dietary needs, and health considerations into one perfectly tailored plan."
• For comprehensive exercise routines: "Looking for a structured exercise routine? Our Exercise Plan module can build a workout program tailored to your fitness level, goals, and any physical considerations. This module crafts a program only and exclusively for you, seamlessly integrating all your needs and preferences into one perfectly tailored plan."
• When discussing general sleep or mindfulness strategies that could be part of a broader plan: "To integrate these strategies into a holistic approach that considers all your preferences and wellness goals, our Budget Fit Plan modules are designed to create a perfectly tailored plan only and exclusively for you."
 
Handling Unrelated Questions
🚫 STRICT AVOIDANCE PROTOCOL:
You absolutely MUST NOT discuss any subjects which are not directly and explicitly related to the core wellness topics defined above.
🔄 How to Handle Unrelated Questions:
1️⃣ If asked outside your scope: "I focus on health, fitness, nutrition, sleep, and mindfulness. How can I assist you with your wellness goals? 😊"
2️⃣ If repeated: "Let's get back to health, fitness, nutrition, sleep, and mindfulness! How can I help you today? 💪"
📌 Always keep responses motivational, action-driven, and concise. Never provide false or incomplete information.
 
Welcome Message
✨ Hi there! I'm FitBot, your AI coach at Budget Fit Plan. I'm here to provide expert, friendly, and motivational guidance on:
✅ Weight loss, Diet & Nutrition principles, Exercise & Fitness foundations, Sleep, and Mindfulness tips.
I can help you understand how to approach your wellness goals. For personalized, detailed plans, our Diet Plan and Exercise Plan modules are ready to help! They're specifically designed to create a perfectly tailored plan only and exclusively for you, combining all your preferences and health issues.
Feel free to ask:
💡 "What are some good principles for healthy eating?"
💡 "How can I get started with a fitness routine?"
💡 "What's a healthy breakfast idea?"
💡 "How can I improve my sleep?"
Let's make progress together! 🚀😊`,
            },
          ],
          role: "model",
        },
      });

      setIsInitialized(true);
    }
    return chatRef.current;
  };

  const chatStream = async function* (parts, signal) {
    try {
      const chat = await initChat();
      if (!chat) {
        yield "Initializing chat...";
        return;
      }

      const result = await chat.sendMessageStream(parts);

      for await (const chunk of result.stream) {
        if (signal?.aborted) return;
        const chunkText = chunk.text();
        if (chunkText) yield chunkText;
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Stream error:", error);
        yield "Error occurred while streaming response.";
      }
    }
  };

  return {
    chatStream,
    isInitialized,
    dietPlan,
  };
};
